import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectActiveWinery, selectUser } from "../context/selectors";
import { setUserRbacPermissions } from "../context/user/userSlice";
import { getUserRbac } from "../services/utils";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";

const useRbacSync = () => {
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const activeWinery = useSelector(selectActiveWinery);
  const user = useSelector(selectUser);
  const { auth } = useAuth();

  useEffect(() => {
    // console.log('🔍 useRbacSync triggered:', {
    //   activeWinery_id: activeWinery?.id,
    //   has_accessToken: !!auth?.accessToken,
    //   user_id: user?.id
    // });

    if (!activeWinery?.id || !auth?.accessToken) {
      // console.log('⚠️ useRbacSync: Missing activeWinery or accessToken, skipping');
      return;
    }

    // console.log('📡 useRbacSync: Fetching RBAC from API for winery', activeWinery.id);

    getUserRbac(axiosPrivate)
      .then((permissions) => {
        dispatch(setUserRbacPermissions(permissions));
      })
      .catch((err) => {
        console.error('❌ useRbacSync: Error fetching RBAC from API:', err.message);

        // ✅ FIXED: Only use fallback if we have rbac_roles in JWT
        // (At login, token doesn't have rbac_roles due to cookie size - they're loaded from API)
        // If API fails AND no rbac_roles in JWT, user has no permissions until API succeeds
        if (!user?.rbac_roles || user.rbac_roles.length === 0) {
          // console.warn('❌ useRbacSync: No fallback available - token has no rbac_roles, waiting for API recovery');
          // Don't set empty permissions - this prevents accidental access denial
          return;
        }

        // console.log('⚠️ useRbacSync: Using fallback with JWT rbac_roles (API unavailable)');
        const resources = {};
        const rolesForWinery = user.rbac_roles.filter(
          (r) => Number(r.winery_id) === Number(activeWinery.id)
        );

        // console.log('📋 useRbacSync fallback: Found roles for winery:', rolesForWinery.length);

        rolesForWinery.forEach((role) => {
          Object.entries(role.permissions || {}).forEach(([resource, perms]) => {
            if (!resources[resource]) {
              resources[resource] = {
                read: false,
                write: false,
                delete: false,
                export: false,
              };
            }

            resources[resource].read ||= perms.read;
            resources[resource].write ||= perms.write;
            resources[resource].delete ||= perms.delete;
            resources[resource].export ||= perms.export;
          });
        });

        // console.log('✅ useRbacSync fallback: Using JWT permissions:', Object.keys(resources));
        dispatch(setUserRbacPermissions(resources));
      });
  }, [activeWinery?.id, auth?.accessToken, user?.rbac_roles, dispatch]);
};

export default useRbacSync;
